import { Modal, Paper } from "@mui/material"
import { useMountEffect } from "@react-hookz/web"
import { trackAnalyticsEvent } from "@web/context/analytics/client"
import { QuotaStatus, useBilling } from "@web/hooks/useBilling"
import { useBoolean } from "@web/hooks/useBoolean"
import { APP_DISPLAY_NAME, Feature, GQL, getQuota, verifyFeatureAccessWithQuotas } from "platform-common/index"
import React, { PropsWithChildren, createContext } from "react"
import { PricingPage } from "../PricingPlansNew"

const MuiOverlay: React.FC<PropsWithChildren<{ onClose: () => void }>> = ({ children, onClose }) => {
  return (
    <Modal open onClose={onClose}>
      <div className="tmb-flex tmb-justify-center tmb-my-16">
        <Paper style={{ maxWidth: "tmb-fit-content" }}>
          <div className="tmb-flex tmb-cursor-pointer tmb-justify-end tmb-pr-1.5 tmb-pt-1.5">
            <svg
              onClick={onClose}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="tmb-w-6 tmb-h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </div>
          {children}
        </Paper>
      </div>
    </Modal>
  )
}

export enum PaidFeatureGuardType {
  ContentGuard = "content guard",
  ButtonGuard = "button guard",
}
interface Props {
  children: React.ReactNode
  feature: Feature
  quotas?: string[]
  type?: PaidFeatureGuardType
  invitationText?: string | React.ReactNode
  OverlayComponent?: React.FC<{ onClose: () => void }>
  requiredProduct?: GQL.AccountType
  ignoreConsumedQuotas?: boolean
}

type PaidFeatureGuardOverlayContext = {
  OverlayContainer: React.FC<React.PropsWithChildren<{ onClose: () => void }>>
}
export const PaidFeatureGuardOverlayContext = React.createContext<PaidFeatureGuardOverlayContext>({
  OverlayContainer: MuiOverlay, // MuiOverlay is default for web (we need to inject BillingModalContainer for extension)
})

/**
 * Context for a component wrapped with the paid feature guard.
 */
interface PaidFeatureGuardContext {
  isFeatureDisabled: boolean
  consumedQuotas: GQL.Quota[]
  featureQuotaStatus?: QuotaStatus
  openPaywallPopup(): void
}
export const PaidFeatureGuardContext = createContext<PaidFeatureGuardContext | null>(null)

export const usePaidFeatureGuardContext = () => {
  const context = React.useContext(PaidFeatureGuardContext)

  if (!context) throw new Error("usePaidFeatureGuardContext must be used within a PaidFeatureGuard")

  return context
}

export const PaidFeatureGuard: React.FC<Props> = ({
  children,
  feature,
  quotas,
  type = PaidFeatureGuardType.ButtonGuard,
  requiredProduct,
  invitationText = `Get 10x return on your investment using ${APP_DISPLAY_NAME} or your money back.`,
  ignoreConsumedQuotas = false,
}) => {
  const [sendNotification] = GQL.useSendSlackNotificationMutation()
  const { OverlayContainer } = React.useContext(PaidFeatureGuardOverlayContext)

  const { state: isModalOpen, enable: openModal, disable: closeModal } = useBoolean(false)

  const { billingStatus, getBillingStatus, billingStatusLoading, getQuotaStatus } = useBilling()

  const billing = billingStatus?.getBillingStatus
  const enabledFeatures = billing?.enabledFeatures ?? []
  const quotaList = billing?.quotas ?? []
  const { hasAccess, consumedQuotas } = verifyFeatureAccessWithQuotas({
    featureName: feature,
    quotasToVerify: quotas,
    billingPlanFeatures: enabledFeatures,
    userQuotas: ignoreConsumedQuotas ? [] : quotaList,
  })

  const isLoadingBillingStatus = billingStatusLoading || !billing
  const isFeatureDisabled = isLoadingBillingStatus ? false : !hasAccess

  const hideContent = type === PaidFeatureGuardType.ContentGuard
  const preventClosing = type === PaidFeatureGuardType.ContentGuard

  useMountEffect(async () => {
    await getBillingStatus()
  })

  React.useEffect(() => {
    if (hideContent) {
      openModal()
    }
  }, [hideContent, openModal])

  const attemptClickOnPaidContent = React.useCallback(
    (clickEvent?: React.MouseEvent) => {
      clickEvent?.preventDefault()
      clickEvent?.stopPropagation()

      trackAnalyticsEvent({
        event: "Paywall Popup",
        requiredProduct,
        feature,
      })
      void sendNotification({
        variables: {
          input: {
            type: "PAYWALL_PRESENTED",
            extraData: JSON.stringify({ feature }),
          },
        },
      })
      openModal()
    },
    [feature, openModal, requiredProduct, sendNotification]
  )

  const attemptCloseModal = React.useCallback(() => {
    if (preventClosing) return
    closeModal()
  }, [closeModal, preventClosing])

  const featureQuotaStatus = getQuotaStatus(feature)

  const wrappedChildren = (
    <PaidFeatureGuardContext.Provider
      value={{
        isFeatureDisabled,
        consumedQuotas,
        openPaywallPopup: attemptClickOnPaidContent,
        featureQuotaStatus,
      }}
    >
      {children}
    </PaidFeatureGuardContext.Provider>
  )

  if (!isFeatureDisabled) {
    return <>{wrappedChildren}</>
  }

  return (
    <>
      {!hideContent && <div onClickCapture={attemptClickOnPaidContent}>{wrappedChildren}</div>}

      {isModalOpen && !billingStatusLoading && (
        <OverlayContainer onClose={attemptCloseModal}>
          <PricingPage
            billingProducts={billingStatus?.listProducts ?? []}
            highlightedProduct={requiredProduct}
            invitationText={invitationText}
          />
        </OverlayContainer>
      )}
    </>
  )
}
